/* Belvoir&Co redesign — motion
   Principles (Ando): nothing moves for decoration. Things arrive the way light enters a room:
   headlines rise out of a mask, images are unveiled from below, grids settle one cell after another,
   and leaving a page is a single slab wipe. Transforms + opacity only. Reduced motion → everything static.
   Markup contract:
     data-bv-reveal="lines|unveil|up|fade"   opt-in on any element
     data-bv-stagger                          on a parent: children reveal in sequence
   Auto-tagging below covers the bv-* sections so section files stay clean. */
(function () {
  'use strict';
  var html = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  html.classList.add('bv-js');

  /* ---------- auto-tag ---------- */
  function tag(sel, type) {
    document.querySelectorAll(sel).forEach(function (el) {
      if (!el.hasAttribute('data-bv-reveal')) el.setAttribute('data-bv-reveal', type);
    });
  }
  function stagger(sel, step, cap) {
    document.querySelectorAll(sel).forEach(function (parent) {
      Array.prototype.forEach.call(parent.children, function (kid, i) {
        if (!kid.hasAttribute('data-bv-reveal')) kid.setAttribute('data-bv-reveal', 'up');
        kid.style.setProperty('--bv-delay', Math.min(i, cap || 8) * (step || 70) + 'ms');
      });
    });
  }
  function autoTag() {
    tag('.bv-display, .bv-h1, .bv-h2', 'lines');
    tag('.bv-section-head .bv-lede, .bv-split__lede, .bv-story__lede, .bv-hero__foot, .bv-cta__foot, .bv-news__form', 'up');
    tag('.bv-media:not(.bv-hero__media):not(.bv-wild__media):not(.bv-split__media):not(.bv-card__media)', 'unveil');
    tag('.bv-split__media, .bv-wild__media', 'fade');
    stagger('.bv-edit__grid, .bv-tiles__grid, .bv-stores__list, .bv-ugc__grid, .bv-steps__list, .bv-trust__list, .bv-facts, .bv-timeline, .bv-news__perks, .bv-cta__proof', 70, 10);
    document.querySelectorAll('[data-bv-stagger]').forEach(function (p) {
      Array.prototype.forEach.call(p.children, function (kid, i) {
        if (!kid.hasAttribute('data-bv-reveal')) kid.setAttribute('data-bv-reveal', 'up');
        kid.style.setProperty('--bv-delay', Math.min(i, 8) * 70 + 'ms');
      });
    });
  }

  /* Headline lines: wrap each line in a mask so it can rise into view. */
  function wrapLines(el) {
    if (el.dataset.bvWrapped) return;
    var lines = el.classList.contains('bv-stagger') ? Array.prototype.slice.call(el.children) : [el];
    lines.forEach(function (line, i) {
      var inner = document.createElement('span');
      inner.className = 'bv-line__inner';
      while (line.firstChild) inner.appendChild(line.firstChild);
      line.appendChild(inner);
      line.classList.add('bv-line');
      line.style.setProperty('--bv-delay', i * 90 + 'ms');
    });
    el.dataset.bvWrapped = '1';
  }

  function init() {
    autoTag();
    var nodes = document.querySelectorAll('[data-bv-reveal]');
    if (reduce || !('IntersectionObserver' in window)) {
      nodes.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    nodes.forEach(function (el) { if (el.getAttribute('data-bv-reveal') === 'lines') wrapLines(el); });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    nodes.forEach(function (el) { io.observe(el); });
    /* Anything already in view reveals on the first frame so the fold is never blank. */
    requestAnimationFrame(function () {
      nodes.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-in');
      });
    });
  }

  /* ---------- page transition: one ink slab ---------- */
  var script = document.currentScript;
  var wantTransition = script && script.getAttribute('data-page-transition') === 'true' && !reduce;
  var slab;
  function ensureSlab() {
    if (slab) return slab;
    slab = document.createElement('div');
    slab.className = 'bv-slab';
    slab.setAttribute('aria-hidden', 'true');
    document.body.appendChild(slab);
    return slab;
  }
  function isInternal(a) {
    if (!a || a.target === '_blank' || a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return false;
    var href = a.getAttribute('href');
    if (!href || href[0] === '#' || /^(mailto|tel|javascript):/.test(href)) return false;
    var u;
    try { u = new URL(a.href, location.href); } catch (e) { return false; }
    if (u.origin !== location.origin) return false;
    if (u.pathname === location.pathname && u.search === location.search) return false; /* same page, hash only */
    if (/\/(cart|checkout|account\/login|account\/logout)/.test(u.pathname)) return false;
    if (a.closest('cart-drawer, quick-cart-drawer, [role="dialog"], .drawer, .header__nav-dropdown')) return false;
    return true;
  }
  function bindTransition() {
    if (!wantTransition) return;
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest('a');
      if (!isInternal(a)) return;
      e.preventDefault();
      var s = ensureSlab();
      try { sessionStorage.setItem('bv-transition', '1'); } catch (err) {}
      /* Speed delta 1 (Oct 2026): the next document is requested the moment the slab starts, so the wipe overlaps
         the network instead of preceding it. Chrome/Edge already prefetched it on pointerdown (speculation rules in
         theme.liquid); for the others this <link rel="prefetch"> starts it now. The wipe itself is shorter (see
         .bv-slab in belvoir-tokens.css) — 300 ms out, 380 ms in — the same move, less wait. */
      try {
        var pf = document.createElement('link'); pf.rel = 'prefetch'; pf.href = a.href; pf.as = 'document';
        document.head.appendChild(pf);
      } catch (err) {}
      html.classList.add('bv-leaving');
      s.classList.add('is-in');
      var done = false;
      function go() { if (done) return; done = true; location.href = a.href; }
      s.addEventListener('transitionend', go, { once: true });
      setTimeout(go, 360); /* never strand the user if transitionend is swallowed */
    }, { capture: false });

    /* Arriving from an internal link: the slab is already covering (set by inline CSS class on <html>), wipe it away. */
    var came = false;
    try { came = sessionStorage.getItem('bv-transition') === '1'; sessionStorage.removeItem('bv-transition'); } catch (err) {}
    if (came) {
      var s = ensureSlab();
      s.classList.add('is-in', 'no-anim');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          s.classList.remove('no-anim');
          s.classList.add('is-out');
          s.addEventListener('transitionend', function () { s.classList.remove('is-in', 'is-out'); }, { once: true });
        });
      });
    }
    /* Back/forward cache restores the page mid-transition: reset. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) { html.classList.remove('bv-leaving'); if (slab) slab.classList.remove('is-in', 'is-out'); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); bindTransition(); });
  else { init(); bindTransition(); }

  /* Sections re-rendered in the theme editor get tagged again. */
  document.addEventListener('shopify:section:load', function () { init(); });
})();
